import { useState, useEffect, useCallback } from 'react';
import type { Conquista, PerfilCompleto } from '../../domain/entities/PerfilJukebox';
import { calcularRankFa, type RankFa } from '../../domain/usecases/calcularRankFa';

interface EstadoVisto {
  nivel:        number;
  conquistaIds: string[];
}

function chaveVisto(clienteId: string): string {
  return `ds:jukebox_perfil_visto:${clienteId}`;
}

function lerEstadoVisto(clienteId: string): EstadoVisto | null {
  try {
    const raw = localStorage.getItem(chaveVisto(clienteId));
    if (!raw) return null;
    return JSON.parse(raw) as EstadoVisto;
  } catch {
    return null;
  }
}

function gravarEstadoVisto(clienteId: string, estado: EstadoVisto): void {
  try {
    localStorage.setItem(chaveVisto(clienteId), JSON.stringify(estado));
  } catch {
    /* localStorage indisponível (modo privado etc.) — celebração só não persiste */
  }
}

export interface PerfilNovidades {
  novoNivel:       RankFa | null;
  novasConquistas: Conquista[];
  marcarComoVisto: () => void;
}

/**
 * Compara o rank e as conquistas atuais do perfil contra o que foi visto na
 * última visita (localStorage, escopado por cliente) pra detectar o que é
 * "novo" e disparar a celebração em ConquistaDesbloqueada.
 *
 * Na primeira visita (sem estado salvo) não celebra nada retroativamente —
 * só grava o estado atual como visto, silenciosamente.
 */
export function usePerfilNovidades(
  clienteId: string | undefined,
  perfil:    PerfilCompleto | null,
): PerfilNovidades {
  const [novoNivel, setNovoNivel]             = useState<RankFa | null>(null);
  const [novasConquistas, setNovasConquistas] = useState<Conquista[]>([]);

  useEffect(() => {
    if (!clienteId || !perfil) return;

    const rankAtual = calcularRankFa(perfil.total_pedidos);
    const idsAtuais = perfil.conquistas.map(c => c.id);
    const visto     = lerEstadoVisto(clienteId);

    if (!visto) {
      gravarEstadoVisto(clienteId, { nivel: rankAtual.nivel, conquistaIds: idsAtuais });
      return;
    }

    if (rankAtual.nivel > visto.nivel) {
      setNovoNivel(rankAtual);
    }

    const idsNovos = idsAtuais.filter(id => !visto.conquistaIds.includes(id));
    if (idsNovos.length > 0) {
      setNovasConquistas(perfil.conquistas.filter(c => idsNovos.includes(c.id)));
    }
  }, [clienteId, perfil]);

  const marcarComoVisto = useCallback(() => {
    if (!clienteId || !perfil) return;
    gravarEstadoVisto(clienteId, {
      nivel: calcularRankFa(perfil.total_pedidos).nivel,
      conquistaIds: perfil.conquistas.map(c => c.id),
    });
    setNovoNivel(null);
    setNovasConquistas([]);
  }, [clienteId, perfil]);

  return { novoNivel, novasConquistas, marcarComoVisto };
}
